import { createAsync, useParams } from '@solidjs/router';
import { For, Show, Switch, Match, createMemo, useContext, createSignal, Suspense, Index } from 'solid-js';
import {
	Button,
	DialogContent,
	DialogAction,
	DialogActions,
	Dialog,
	Errors,
	Form,
	Link,
	DialogTrigger,
	Container,
	Heading,
	Section,
	Grid,
	GridFive,
	GridSeven,
	Receipt,
	AccessCircleLogo,
} from '@troon/ui';
import { Title } from '@solidjs/meta';
import { useTrackEvent } from '@troon/analytics';
import { twJoin } from '@troon/tailwind-preset/merge';
import { IconSquareWarning } from '@troon/icons/square-warning';
import { IconCalendar } from '@troon/icons/calendar';
import dayjs from '@troon/dayjs';
import { cachedGet } from '@troon/api-client';
import { ReservationState, ReservationUserState, gql, mutationAction, useMutation } from '../../../graphql';
import { useUser } from '../../../providers/user';
import { ReservationCtx } from '../../../providers/reservation';
import { FacilityLocation } from '../../../components/facility/location';
import { FacilityReservationHeader } from '../../../components/facility/reservation-header';
import { Hero } from '../../../components/hero/photo';
import { MessageBanner } from '../../../components/message-banner';
import { FrequentlyAskedQuestions } from '../../../components/faqs';
import { ReservationAccessUpsell } from '../../../components/upsells/access-reservation';
import { Banner } from '../../../components/upsells/banner';
import { ReservationPlayerSlot } from './components/reservation-player';
import type { ApiResponse } from '@troon/api-client';
import type { RouteSectionProps } from '@solidjs/router';

export default function Reservation(props: RouteSectionProps) {
	const params = useParams();
	const user = useUser();
	const data = useContext(ReservationCtx)!;
	const track = useTrackEvent();

	const acceptAction = useMutation(handleAccept);
	const rejectAction = useMutation(handleReject);
	const leaveAction = useMutation(handleLeave);

	const paymentInfo = createAsync(() => getPaymentInfo({ path: { id: props.params.reservationId! } }));

	const [showCancelConfirm, setShowCancelConfirm] = createSignal(false);

	const reservationUser = createMemo(() => {
		return data()?.reservation.users.find((resUser) => resUser.user?.id === user()?.id);
	});

	const canModify = createMemo(() => {
		return !!(
			data.latest?.reservation.owner.id === user()?.id &&
			data.latest?.reservation.state !== 'CANCELLED' &&
			(paymentInfo.latest?.cancellationInfo.isCancellable || dayjs().isBefore(data.latest?.reservation.cancelBy?.epoch))
		);
	});

	const isPastStartTime = createMemo(() => dayjs().isSameOrAfter(data.latest?.reservation.teeTime.epoch));

	const reservationUsers = createMemo(() =>
		data.latest?.reservation.users
			.sort((a, b) => {
				if (a.user?.id === data.latest?.reservation.owner.id || b.state === 'EMPTY') {
					return -1;
				}
				if (b.user?.id === data.latest?.reservation.owner.id || a.state === 'EMPTY') {
					return 1;
				}
				return sort(
					`${a.user?.firstName ?? ''} ${a.user?.lastName ?? ''}`,
					`${b.user?.firstName ?? ''} ${b.user?.lastName ?? ''}`,
				);
			})
			.map((u, i) => [u, i] as [ApiResponse<'get', '/v0/reservations/{id}'>['users'][number], number]),
	);

	return (
		<>
			<Hero src={data()?.reservation.facility?.hero}>
				<Show when={data()?.reservation.state === ReservationState.Cancelled}>
					<MessageBanner variant="subtle" class="absolute inset-x-0 bottom-5">
						<p>This tee time has been cancelled.</p>
					</MessageBanner>
				</Show>
			</Hero>

			<Container size={data()?.reservation.state === ReservationState.Cancelled ? 'small' : undefined}>
				<>
					<Title>
						Tee time reservation at{' '}
						{data.latest?.reservation.facility.name ?? data.latest?.reservation.course.name ?? ''} | Golf with Access
					</Title>

					<Show when={data.latest?.reservation}>
						{(reservation) => (
							<FacilityReservationHeader
								bordered
								epoch={reservation().teeTime.epoch}
								timezone={reservation().teeTime.tz}
								slug={reservation().facility.slug}
								name={reservation().facility.name}
								holeCount={reservation().holeCount}
								playerCount={reservation().playerCount}
								includesCart={reservation().includesCart}
								courseName={reservation().course.name}
							>
								<div class="flex flex-row flex-wrap items-stretch justify-end gap-4">
									<Switch>
										<Match when={isPastStartTime() || data.latest?.reservation.state === ReservationState.Cancelled}>
											{null}
										</Match>
										<Match when={reservationUser()?.state === ReservationUserState.Invited}>
											<Form action={acceptAction} document={acceptInvitationMutation} method="post">
												<input type="hidden" name="reservationUserId" value={reservationUser()?.id} />
												<Errors />
												<Button type="submit">Accept invitation</Button>
											</Form>
											<Form action={rejectAction} document={rejectInvitationMutation} method="post">
												<input type="hidden" name="reservationUserId" value={reservationUser()?.id} />
												<input type="hidden" name="__reservationId" value={params.reservationId} />
												<Errors />
												<Button appearance="secondary" action="danger" type="submit">
													Reject invitation
												</Button>
											</Form>
										</Match>
										<Match
											when={
												data.latest &&
												paymentInfo.latest?.cancellationInfo.isCancellable &&
												paymentInfo.latest?.cancellationInfo
											}
										>
											{(cancellationInfo) => (
												<Dialog key="reservation-cancel" open={showCancelConfirm()} onOpenChange={setShowCancelConfirm}>
													<DialogTrigger appearance="secondary" class="shrink md:grow-0">
														Cancel tee time
													</DialogTrigger>
													<DialogContent header="Cancel reservation" headerLevel="h2">
														<CancelReservation
															id={reservation().id}
															phone={reservation().facility.phone ?? ''}
															cancelBy={cancellationInfo().cancelBy?.epoch}
															cancellationPolicy={cancellationInfo().displayMessage}
															onSuccess={() => {
																setShowCancelConfirm(false);
															}}
															onCancel={() => {
																setShowCancelConfirm(false);
															}}
														/>
													</DialogContent>
												</Dialog>
											)}
										</Match>
										<Match when={data.latest?.reservation.state === 'ACTIVE'}>
											<Dialog key="reservation-leave">
												<DialogTrigger appearance="secondary" disabled={isPastStartTime()} class="shrink md:grow-0">
													Leave reservation
												</DialogTrigger>
												<DialogContent header="Leave reservation" headerLevel="h2">
													<Form action={leaveAction} document={leaveReservationMutation} method="post">
														<input type="hidden" name="reservationUserId" value={reservationUser()?.id} />
														<input type="hidden" name="__reservationId" value={params.reservationId} />
														<p>Are you sure you want to leave this reservation?</p>
														<Errors />

														<DialogActions>
															<DialogAction action="danger" type="submit">
																Leave reservation
															</DialogAction>
														</DialogActions>
													</Form>
												</DialogContent>
											</Dialog>
										</Match>
									</Switch>
									<Show
										when={
											reservationUser()?.state !== 'INVITED' &&
											data.latest?.reservation.state !== 'CANCELLED' &&
											!isPastStartTime()
										}
									>
										<Dialog key="reservation-calendar-event">
											<DialogTrigger appearance="primary" class="shrink grow-0">
												<IconCalendar /> <span class="sr-only">Calendar</span>
											</DialogTrigger>
											<DialogContent header="Add to calendar" headerLevel="h2">
												<div class="grid grid-cols-2 gap-4">
													<For each={Object.entries(data.latest?.reservation.calendarEvent ?? {})}>
														{([key, href]) => (
															<Button
																appearance={calendarImage[key] ? 'secondary' : 'primary'}
																as={Link}
																href={href}
																rel="noopener noreferer"
																onClick={() => {
																	track('addToCalendar', { calendarType: key });
																}}
																class={
																	calendarImage[key] ? 'flex flex-col items-center gap-2 normal-case' : 'col-span-2'
																}
															>
																<Show when={calendarImage[key]}>
																	{(src) => <img width="84" height="84" src={src()} alt="" />}
																</Show>
																{calendarTitle[key] ?? ''}
															</Button>
														)}
													</For>
												</div>
											</DialogContent>
										</Dialog>
									</Show>
								</div>
							</FacilityReservationHeader>
						)}
					</Show>

					<Grid>
						<GridSeven
							class={twJoin('flex flex-col gap-12', data()?.reservation.state === 'CANCELLED' && 'lg:col-span-12')}
						>
							<Suspense>
								<Show when={data()?.reservation.state !== 'CANCELLED' && data()?.owner?.accessUpsell}>
									{(upsell) => <ReservationAccessUpsell savings={upsell().savings.displayValue} />}
								</Show>
							</Suspense>

							<Section layout="tight">
								<Show when={data()?.reservation.facility.supportsAccessPoints && !user()?.accessRewardsOptIn}>
									<Banner
										appearance="light"
										size="sm"
										actionHref="/account/transfer-points"
										location="reservation"
										actionText="Transfer"
										product="points"
									>
										<p class="text-xs">
											You need to transfer your Troon Rewards points to Access to earn points for this round.
										</p>
									</Banner>
								</Show>
								<Heading as="h2" size="h4">
									Invite & Manage Players
								</Heading>
								<p>
									Invite the other players in your group to join this reservation. We’ll send you a notification when
									they join.
								</p>

								<ul class="flex flex-col gap-2">
									<Index each={reservationUsers()}>
										{(reservationUser) => (
											<li class="rounded-lg border border-neutral p-4 md:py-6">
												<ReservationPlayerSlot
													userSlot={reservationUser()[1] + 1}
													canModify={canModify()}
													user={reservationUser()[0].user}
													userState={reservationUser()[0].state}
													guestPass={reservationUser()[0].guestPass}
													isHost={reservationUser()[0].user?.id === data.latest?.reservation.owner.id}
													availableGuestPass={
														data()?.owner?.guestPasses.available[0]
															? {
																	id: data()!.owner!.guestPasses.available[0]!.id,
																	status: data()!.owner!.guestPasses.available[0]!.status,
																}
															: undefined
													}
													guestPassDiscount={data()?.reservation.guestPassDiscount?.displayValue}
													rateType={reservationUser()[0].rateType}
													reservationUserId={reservationUser()[0].id}
													reservationId={props.params.reservationId!}
													hostRateType={data.latest?.reservation.bookingRateType ?? ''}
												/>
											</li>
										)}
									</Index>
								</ul>
							</Section>

							<Section layout="tight">
								<Heading as="h2" size="h4">
									Frequently Asked Questions
								</Heading>
								<div class="rounded-lg border border-neutral">
									<FrequentlyAskedQuestions
										id={
											(data()?.reservation.playerCount ?? 0) === 1
												? 'access-individual-booking-faqs'
												: 'access-reservation-details-multiple-player-faqs'
										}
									/>
								</div>
							</Section>

							<Section layout="tight">
								<Heading as="h2" size="h4">
									Facility info
								</Heading>
								<Show when={data.latest?.reservation.facility}>
									{(facility) => (
										<FacilityLocation
											address={facility().address}
											courses={[data.latest!.reservation.course.name]}
											slug={facility().slug}
											name={facility().name}
											phone={facility().phone}
											website={facility().website}
										/>
									)}
								</Show>
							</Section>
						</GridSeven>

						<Show when={data.latest?.reservation.state !== 'CANCELLED'}>
							<GridFive>
								<div class="sticky top-24 flex flex-col gap-y-8">
									<div class="flex flex-col gap-y-4 rounded-lg border border-neutral-500 p-4 md:p-8">
										<div class="flex flex-col gap-4">
											<Heading as="h2" size="h5">
												Payment Info
											</Heading>

											<Show when={paymentInfo.latest?.receipt}>
												{(receipt) => (
													<Receipt
														items={receipt().items.map((i) => ({ label: i.label, amount: i.displayAmount }))}
														totals={receipt().totals.map((i) => ({ label: i.label, amount: i.amount.displayValue }))}
														totalDisclaimer="All amounts displayed are subject to additional taxes and fees, which may be added at check in."
														disclaimer={receipt().disclaimer || undefined}
														reimbursement={
															receipt().reimbursement
																? {
																		label: 'Reimbursed at check-in',
																		amount: receipt().reimbursement!.amount?.displayValue,
																		disclaimer: receipt().reimbursement!.disclaimer,
																	}
																: undefined
														}
													/>
												)}
											</Show>

											<Suspense>
												<Show when={paymentInfo.latest?.accessPointsEarned}>
													{(points) => (
														<p class="flex items-center gap-4 rounded-lg bg-brand-100 p-4 text-sm text-brand-600">
															<AccessCircleLogo class="size-8 shrink-0 text-brand" />
															<span>
																You’ll earn <b>{points()}</b> points when you pay for this round.
															</span>
														</p>
													)}
												</Show>
											</Suspense>
										</div>
									</div>

									<div class="flex flex-col gap-2">
										<Heading as="h3" size="h5">
											<IconSquareWarning class="text-2xl text-brand" /> Cancellation policy
										</Heading>
										<p>{paymentInfo.latest?.cancellationInfo.displayMessage}</p>
									</div>
								</div>
							</GridFive>
						</Show>
					</Grid>
				</>
			</Container>
		</>
	);
}

const getPaymentInfo = cachedGet('/v1/reservations/{id}/payment-info');

type CancelProps = {
	id: string;
	phone: string;
	onSuccess: () => void;
	onCancel: () => void;
	cancelBy?: number;
	cancellationPolicy?: string;
};

function CancelReservation(props: CancelProps) {
	const handleCancel = useMutation(
		mutationAction(cancelReservationMutation, {
			revalidates: ['/v0/reservations/{id}', '/v1/reservations/{id}/payment-info'],
			toast: 'Your tee time has been cancelled.',
			// eslint-disable-next-line solid/reactivity
			onSuccess: props.onSuccess,
			track: {
				event: 'cancelReservation',
				// eslint-disable-next-line solid/reactivity
				data: { id: props.id },
			},
		}),
	);

	return (
		<Switch
			fallback={
				<div class="flex flex-col gap-8">
					<p>
						It is too late to cancel this tee time according to the cancellation policy. Please call the golf shop
						instead at <span class="font-semibold">{props.phone}</span>.
					</p>
					<Button as={Link} href={`tel:${props.phone}`}>
						Call golf shop
					</Button>
				</div>
			}
		>
			<Match when={!props.cancelBy || dayjs().isBefore(props.cancelBy)}>
				<Form action={handleCancel} document={cancelReservationMutation} method="post">
					<p>Are you sure you want to cancel this reservation? There is no guarantee you’ll be able to rebook.</p>
					<Show when={props.cancellationPolicy}>{(policy) => <p>{policy()}</p>}</Show>
					<input type="hidden" name="id" value={props.id} />
					<Errors />
					<DialogActions>
						<DialogAction type="submit" action="danger">
							Confirm cancellation
						</DialogAction>
						<DialogAction appearance="transparent" type="button" onClick={props.onCancel}>
							Nevermind
						</DialogAction>
					</DialogActions>
				</Form>
			</Match>
		</Switch>
	);
}

export const route = { info: { nav: { hero: true } } };

const sort = new Intl.Collator('en').compare;

const cancelReservationMutation = gql(`
mutation cancelReservation($id: String!) {
	cancelReservation(id: $id) {
		id
	}
}`);

const acceptInvitationMutation = gql(`mutation acceptReservationInvite($reservationUserId: String!) {
	acceptReservationInvite(reservationUserId: $reservationUserId) { id }
}`);

const handleAccept = mutationAction(acceptInvitationMutation, {
	revalidates: ['/v0/reservations/{id}', '/v1/reservations/{id}/payment-info'],
	track: {
		event: 'acceptInvitation',
		transform: (data) => ({ reservationUserId: data.get('reservationUserId') as string }),
	},
});

const rejectInvitationMutation = gql(`mutation rejectReservationInvite($reservationUserId: String!) {
	rejectReservationInvite(reservationUserId: $reservationUserId) { ok }
}`);

const handleReject = mutationAction(rejectInvitationMutation, {
	redirect: '/',
	toast: 'You have declined this invitation.',
	track: {
		event: 'rejectInvitation',
		transform: (data) => ({ reservationId: data.get('__reservationId') as string }),
	},
});

const leaveReservationMutation = gql(`
mutation leaveReservation($reservationUserId: String!) {
	leaveReservation(reservationUserId: $reservationUserId) {
		ok
	}
}`);

const handleLeave = mutationAction(leaveReservationMutation, {
	redirect: '/',
	toast: 'You have left this reservation.',
	track: {
		event: 'leaveReservation',
		transform: (data) => ({ reservationId: data.get('__reservationId') as string }),
	},
});

const calendarImage: Record<string, string> = {
	google: '/assets/calendar/google-calendar.svg',
	office365: '/assets/calendar/office-365.svg',
	outlook: '/assets/calendar/outlook.svg',
	yahoo: '/assets/calendar/yahoo.svg',
};

const calendarTitle: Record<string, string> = {
	google: 'Google',
	office365: 'Office 365',
	outlook: 'Outlook',
	yahoo: 'Yahoo',
	ics: 'Download .ics',
};
